import { useEffect, useMemo, useRef, useState } from "react";
import SignaturePad from "react-signature-pad-wrapper";
import { Edit } from "lucide-react";
import t from "@/lang/lang";
import { useBranchesBranchShowQuery } from "@/services/backend/branch/api/branch";
import {
  useQpmOfferClearSignatureMutation,
  useQpmOfferSignatureQuery,
  useQpmOfferSignMutation,
} from "@/services/backend/qpm/offers/api/offer-api";
import {
  Offer,
  OfferSignature,
  SignRequest,
} from "@/services/backend/qpm/offers/types/offer";
import { InputErrorMessages } from "@/shared/components/errors/input-error-messages";
import { Button } from "@/shared/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { NullUUID } from "@/shared/lib/utilities/uuid";

const signatureCanvasHeight = 260;

interface SignatureData {
  name: string;
  signature: string;
  signatureLocation: string;
  signatureDate: string;
}

function toDateInputValue(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  return value.slice(0, 10);
}

function signatureLocationDefault(offer: Offer, branchName: string): string {
  if (offer.signatureLocation && offer.signatureLocation.trim() !== "") {
    return offer.signatureLocation;
  }
  return branchName;
}

function signatureDateDefault(offer: Offer): string {
  return toDateInputValue(offer.signatureDate) || toDateInputValue(offer.date);
}

export function SignatureButton(props: {
  offer: Offer;
  name?: string;
  showError?: boolean;
  disabled?: boolean;
  triggerLabel?: string;
}) {
  const { offer, name, showError, disabled, triggerLabel } = props;
  const { data: branch } = useBranchesBranchShowQuery(
    { id: offer.branchId || NullUUID },
    { skip: !offer.branchId || offer.branchId === "" },
  );
  const branchName = branch?.name ?? "";
  const defaultLocation = useMemo(
    () => signatureLocationDefault(offer, branchName),
    [branchName, offer],
  );
  const defaultDate = useMemo(() => signatureDateDefault(offer), [offer]);
  const [request, setRequest] = useState<SignRequest>({
    id: offer.id,
    signature: "",
    dataFormat: "image/svg+xml:base64",
    nameOfSigner: "",
    signatureLocation: defaultLocation,
    signatureDate: defaultDate,
  });
  const [sign, { isLoading, error, isSuccess, reset }] =
    useQpmOfferSignMutation();
  const [clearSignature, clearSignatureState] =
    useQpmOfferClearSignatureMutation();
  const { data: currentSignature } = useQpmOfferSignatureQuery(
    { id: offer.id },
    { skip: !offer.signatureId },
  );

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    sign,
    isLoading,
    error,
    isSuccess,
    reset,
    1,
  );

  return (
    <div
      className={
        showError
          ? "[&_button]:border-destructive [&_button]:text-destructive"
          : undefined
      }
    >
      <QpmSignatureDialog
        title={t("Angebot bestätigen")}
        name={name || ""}
        defaultLocation={defaultLocation}
        defaultDate={defaultDate}
        minDate={toDateInputValue(offer.date)}
        disabled={disabled}
        toolTipText={t("Angebot Unterschreiben")}
        triggerLabel={triggerLabel}
        currentSignature={currentSignature}
        clearSignatureLoading={clearSignatureState.isLoading}
        onClearSignature={
          offer.signatureId && !disabled
            ? async () => {
                await clearSignature({ offerId: offer.id }).unwrap();
              }
            : undefined
        }
        onConfirm={(data) => {
          if (disabled) {
            return;
          }
          setRequest({
            ...request,
            signature: data.signature,
            nameOfSigner: data.name,
            signatureLocation: data.signatureLocation,
            signatureDate: data.signatureDate,
          });
        }}
      />
    </div>
  );
}

function QpmSignatureDialog(props: {
  title: string;
  name: string;
  defaultLocation: string;
  defaultDate: string;
  minDate: string;
  onConfirm: (data: SignatureData) => void;
  disabled?: boolean;
  toolTipText?: string;
  triggerLabel?: string;
  currentSignature?: OfferSignature;
  clearSignatureLoading?: boolean;
  onClearSignature?: () => Promise<void>;
}) {
  const {
    title,
    name,
    defaultLocation,
    defaultDate,
    minDate,
    onConfirm,
    disabled,
    toolTipText,
    triggerLabel,
    currentSignature,
    clearSignatureLoading,
    onClearSignature,
  } = props;
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<SignatureData>({
    name,
    signature: "",
    signatureLocation: defaultLocation,
    signatureDate: defaultDate,
  });
  const [nameErrors, setNameErrors] = useState<string[]>([]);
  const [sigErrors, setSigErrors] = useState<string[]>([]);
  const [locationErrors, setLocationErrors] = useState<string[]>([]);
  const [dateErrors, setDateErrors] = useState<string[]>([]);
  const ref = useRef<SignaturePad>(null);
  const signerName = currentSignature?.nameOfSigner || name;

  useEffect(() => {
    if (open) {
      setData((current) => ({
        ...current,
        name: signerName,
        signatureLocation: defaultLocation,
        signatureDate: defaultDate,
      }));
    }
  }, [defaultDate, defaultLocation, open, signerName]);

  const clear = () => {
    setSigErrors([]);
    const pad = ref.current;
    if (!pad) {
      return;
    }
    pad.instance.clear();
  };

  const signatureToDataUrl = () => {
    const pad = ref.current?.instance;
    if (!pad || pad.isEmpty()) {
      return null;
    }
    return pad.toDataURL("image/svg+xml");
  };

  const validationPasses = () => {
    const signature = signatureToDataUrl();
    const nextSigErrors: string[] = [];
    const nextNameErrors: string[] = [];
    const nextLocationErrors: string[] = [];
    const nextDateErrors: string[] = [];

    if (!signature) {
      nextSigErrors.push("Bitte unterschreiben Sie.");
    }
    if (!data.name || data.name.length === 0) {
      nextNameErrors.push("Bitte geben Sie Ihren Namen ein.");
    }
    if (!data.signatureLocation || data.signatureLocation.trim().length === 0) {
      nextLocationErrors.push("Bitte geben Sie den Ort ein.");
    }
    if (!data.signatureDate) {
      nextDateErrors.push("Bitte geben Sie das Datum ein.");
    } else if (minDate && data.signatureDate < minDate) {
      nextDateErrors.push(
        `Das Datum darf nicht vor dem Angebotsdatum ${minDate} liegen.`,
      );
    }

    setSigErrors(nextSigErrors);
    setNameErrors(nextNameErrors);
    setLocationErrors(nextLocationErrors);
    setDateErrors(nextDateErrors);
    return (
      nextSigErrors.length === 0 &&
      nextNameErrors.length === 0 &&
      nextLocationErrors.length === 0 &&
      nextDateErrors.length === 0
    );
  };

  const close = () => {
    setData({
      name: signerName,
      signature: "",
      signatureLocation: defaultLocation,
      signatureDate: defaultDate,
    });
    setNameErrors([]);
    setSigErrors([]);
    setLocationErrors([]);
    setDateErrors([]);
    clear();
    setOpen(false);
  };

  const confirm = () => {
    if (!validationPasses()) {
      return;
    }
    const signature = signatureToDataUrl();
    onConfirm({
      ...data,
      signature: signature!,
      signatureLocation: data.signatureLocation.trim(),
    });
    close();
  };

  const triggerButton = (
    <Button
      variant="outline"
      onClick={() => setOpen(true)}
      disabled={disabled}
      className={triggerLabel ? "w-full justify-start" : undefined}
    >
      {triggerLabel || <Edit className="h-5 w-5" />}
    </Button>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpenState) => {
        if (newOpenState) {
          setOpen(newOpenState);
        } else {
          close();
        }
      }}
    >
      {toolTipText && !triggerLabel ? (
        <Tooltip>
          <DialogTrigger asChild>
            <TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
          </DialogTrigger>
          <TooltipContent>
            <p>{toolTipText}</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        <DialogTrigger asChild>{triggerButton}</DialogTrigger>
      )}

      <DialogContent className="flex max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-5xl flex-col overflow-hidden sm:max-h-[calc(100dvh-2rem)] sm:w-[min(92vw,72rem)] sm:!max-w-5xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="grid flex-1 gap-4 overflow-y-auto pr-1">
          {currentSignature?.data ? (
            <div className="rounded border p-3">
              <div className="mb-2 font-bold">{t("Aktuelle Unterschrift")}</div>
              <img
                src={currentSignature.data}
                alt={t("Aktuelle Unterschrift")}
                className="h-32 max-w-full object-contain object-left"
              />
              {!disabled && onClearSignature ? (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="mt-3">
                      {t("Unterschrift entfernen")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t("Unterschrift entfernen?")}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t(
                          "Die gespeicherte Unterschrift, der Ort und das Datum werden aus dem Angebot entfernt.",
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("Abbrechen")}</AlertDialogCancel>
                      <AlertDialogAction
                        disabled={clearSignatureLoading}
                        onClick={() => {
                          void onClearSignature().then(close);
                        }}
                      >
                        {t("Unterschrift entfernen")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              ) : null}
            </div>
          ) : null}
          <div>
            <Input
              value={data.name}
              placeholder="Name des Unterzeichners"
              onChange={(e) => {
                setData({ ...data, name: e.target.value });
                setNameErrors([]);
              }}
            />
            <InputErrorMessages errors={nameErrors} />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Input
                value={data.signatureLocation}
                placeholder="Ort"
                onChange={(e) => {
                  setData({ ...data, signatureLocation: e.target.value });
                  setLocationErrors([]);
                }}
              />
              <InputErrorMessages errors={locationErrors} />
            </div>
            <div>
              <Input
                type="date"
                min={minDate}
                value={data.signatureDate}
                onChange={(e) => {
                  setData({ ...data, signatureDate: e.target.value });
                  setDateErrors([]);
                }}
              />
              <InputErrorMessages errors={dateErrors} />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-3">
              <div className="font-bold">{t("Bitte hier Unterschreiben:")}</div>
              <Button variant="outline" size="sm" onClick={clear}>
                {t("Unterschrift zurücksetzen")}
              </Button>
            </div>
            <div
              className={
                sigErrors.length > 0
                  ? "border-destructive bg-destructive-foreground relative mt-2 h-[min(42dvh,260px)] min-h-48 overflow-hidden rounded border sm:h-[min(46dvh,300px)]"
                  : "bg-background relative mt-2 h-[min(42dvh,260px)] min-h-48 overflow-hidden rounded border sm:h-[min(46dvh,300px)]"
              }
            >
              <div className="border-muted-foreground pointer-events-none absolute inset-x-4 bottom-14 z-10 border-t" />
              <div className="ring-muted pointer-events-none absolute inset-0 z-10 rounded ring-1 ring-inset" />
              <SignaturePad
                ref={ref}
                height={signatureCanvasHeight}
                canvasProps={{
                  className: "h-full w-full touch-none",
                }}
                redrawOnResize
              />
            </div>
            <InputErrorMessages errors={sigErrors} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            {t("Abbrechen")}
          </Button>
          <Button onClick={confirm}>{t("Bestätigen")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
